import { snakeCase } from "lodash";
import numeral from "numeral";
import { Suspense, useRef } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { NotificationsLevel } from "../lib/enums";
import ManageHashSubscriptionDialog from "../modules/alertsconfiguration/managesubscription/ManageHashSubscriptionDialog";
import UrlFalseReportModal from "../modules/network/components/header/UrlFalseReportModal";
import FalseReportModal from "../modules/samples/components/false-report-modal/FalseReportModal";
import Reanalyze from "../modules/samples/components/reanalyze/reanalyze-popup/Reanalyze";
import * as notifications from "../notifications/notifications";
import api from "../services/api";
import { consts, route } from "../shim/jsbridge";
import tcbase from "../shim/tcbase";
import { deletableSamples } from "../lib/deletableSamples";
import bulkDownload from "../tcbase/bulk_download";
import ActionsModalSelectionsMessage from "./actions/ActionsModalSelectionsMessage";
import LoadingOverlay from "./LoadingOverlay";
import PreviewVisualizationModal from "./PreviewVisualizationModal";
import RenderWhen from "./RenderWhen";
import SubmitUrlModal from "./SubmitUrlModal";
import TagsModalChooser from "./tags/containers/TagsModalChooser";
import UserClassification from "./user-classification/UserClassification";

const { userName, isSuperuser } = tcbase || {};

export const deletableBulkSamples = samples =>
  deletableSamples(samples || [], userName, isSuperuser);

export const ActionsModalNames = {
  Download: "download",
  DownloadBulk: "download-bulk",
  DownloadUnpacked: "download-unpacked",
  DownloadBulkUnpacked: "download-bulk-unpacked",
  UserClassification: "user-classification",
  BulkUserClassification: "bulk-user-classification",
  Subscribehashes: "subscribehashes",
  Unsubscribehashes: "unsubscribehashes",
  Reanalyze: "reanalyze",
  Delete: "delete",
  Recrawl: "recrawl",
  RetryURL: "retryurl",
  ApplyTags: "applytags",
  Visualization: "visualization",
  ReportFalseClassification: "report-false-classification",
  ReportUrlFalseClassification: "report-url-false-classification"
};

export const BULK_ACTION_LABELS = {
  Reanalyze: "Reanalyze",
  DownloadSamples: "Download Samples",
  DownloadExtractedFiles: "Download Extracted Files",
  ApplyUserTags: "Apply User Tags",
  SetClassification: "Set Classification",
  Delete: "Delete",
  FetchAndAnalyze: "Fetch & Analyze",
  FetchAndAnalyzeAdvanced: "Fetch & Analyze (Advanced)",
  Subscribe: "Subscribe",
  Unsubscribe: "Unsubscribe"
};

const onBulkDownload = (e, unpacked, activeModalData) => {
  e.preventDefault();

  const { samples } = activeModalData;
  const password = e.target[0].value;

  return bulkDownload.initDownload(samples, password, unpacked);
};

const onDownloadSample = (e, unpacked, { sha1, file_names, isContainer }) => {
  e.preventDefault();

  let filename = file_names?.at(0) || sha1;
  const password = e.target[0].value;
  const routeName = unpacked
    ? "api-download-unpacked"
    : "download_filename_url";

  const fileLevel = unpacked
    ? "Unpacked archive of"
    : isContainer
      ? "Container"
      : "Sample";

  return api
    .checkSampleDownload({
      hash: sha1,
      filename: encodeURIComponent(filename),
      zip: encodeURIComponent(password),
      unpacked
    })
    .then(() => {
      if (filename.endsWith(".rl")) filename = filename.slice(0, -3);

      window.open(
        route(routeName, [sha1], {
          filename: encodeURIComponent(filename),
          zip: encodeURIComponent(password)
        }),
        "_blank"
      );
      notifications.show({
        level: "success",
        message: `${fileLevel} ${filename} is downloading`
      });
    })
    .catch(() =>
      notifications.show({
        level: "error",
        message: `Failed to download ${filename}`
      })
    );
};

const onDeleteSample = (hash, onSuccess) =>
  api
    .deleteUploadSample({ hash })
    .then(() => {
      notifications.show({
        level: NotificationsLevel.INFO,
        message: "Sample deleted"
      });
      onSuccess?.();
    })
    .catch(err =>
      notifications.show({
        level: NotificationsLevel.ERROR,
        message: err.data.message
      })
    );

const onBulkDeleteSample = (hashes, onSuccess) =>
  api
    .bulkDeleteUploadSamples({ hashes })
    .then(({ data }) => {
      const {
        valid_deletions: validDeletions,
        invalid_deletions: invalidDeletions,
        error_messages: errorMessages
      } = data ?? {};

      const validDeletionsMessage = validDeletions.length
        ? `Successfully deleted ${validDeletions?.length} ${"sample".pluralize(
            validDeletions?.length
          )}. `
        : "";

      const invalidDeletionsMessage = invalidDeletions.length
        ? `Failed to delete ${invalidDeletions?.length} ${"sample".pluralize(
            invalidDeletions?.length
          )} with ${"error".pluralize(
            errorMessages?.length
          )}: ${errorMessages.join(", ")}`
        : "";

      const hasValidDeletions = validDeletions.length > 0;
      const hasInvalidDeletions = invalidDeletionsMessage.length > 0;

      if (hasValidDeletions) {
        notifications.show({
          level: NotificationsLevel.INFO,
          message: validDeletionsMessage
        });
      }

      if (hasInvalidDeletions) {
        const level = hasValidDeletions
          ? NotificationsLevel.WARNING
          : NotificationsLevel.ERROR;

        notifications.show({
          level,
          message: invalidDeletionsMessage
        });
      }

      const hashesNotDeleted = invalidDeletions?.map(item => item.hash);

      onSuccess(hashesNotDeleted);
    })
    .catch(err => {
      notifications.show({
        level: NotificationsLevel.ERROR,
        message: err?.data?.message ?? `Failed to delete samples.`
      });
    });

const onBulkDeleteAllUploads = (data, onSucceess) =>
  api
    .bulkDeleteAllUploads(data)
    .then(({ data }) => {
      if (!data) {
        notifications.show({
          level: NotificationsLevel.ERROR,
          message: "Samples could not be deleted!"
        });
      } else {
        notifications.show({
          level: NotificationsLevel.INFO,
          message: `All samples from selection were deleted.`
        });
      }
      onSucceess();
    })
    .catch(err =>
      notifications.show({
        level: NotificationsLevel.ERROR,
        message: err.data.message
      })
    );

export const DeleteModal = ({ message, onSubmit, onClose }) => (
  <Modal show size="sm" onHide={onClose} data-testid="delete_sample_modal">
    <Modal.Header closeButton>
      <Modal.Title>Delete Sample</Modal.Title>
    </Modal.Header>
    <Form
      onSubmit={() => {
        onSubmit();
        onClose();
      }}
    >
      <Modal.Body data-testid="delete_sample_modal_message">
        {message}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          variant="prompt-secondary"
          data-testid="delete_sample_modal_delete_button"
        >
          Delete
        </Button>
        <Button
          type="button"
          variant="prompt-primary"
          onClick={onClose}
          data-testid="delete_sample_modal_cancel_button"
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

const handleDeleteSingle = (hash, onSuccess, onClose) => {
  const message = "Are you sure you want to delete this sample?";
  return (
    <DeleteModal
      message={message}
      onSubmit={() => onDeleteSample(hash, onSuccess)}
      onClose={onClose}
    />
  );
};

const handleDeleteBulk = (
  hashes = [],
  onSuccess = () => {},
  onClose = () => {}
) => {
  // Count the payload, not the selection: cloud rows and extracted files are
  // filtered out.
  const deletable = deletableBulkSamples(hashes);
  const payload = deletable.map(hash => hash.sha1);
  const message = `This will delete ${payload.length} ${"sample".pluralize(
    payload.length
  )} in selection!
      \nAre you sure you wish to proceed?`;

  return (
    <DeleteModal
      message={message}
      onSubmit={() => onBulkDeleteSample(payload, onSuccess)}
      onClose={onClose}
    />
  );
};

const handleDeleteAll = (data, onSuccess, onClose) => {
  const message = `You have selected all samples.\nThis will delete ${
    isSuperuser ? "" : "your"
  } samples in selection!\nAre you sure you wish to proceed?`;

  return (
    <DeleteModal
      message={message}
      onSubmit={() => onBulkDeleteAllUploads(data, onSuccess)}
      onClose={onClose}
    />
  );
};

const ActionsModal = ({
  modalName,
  activeModalData,
  toggleModal,
  onSubmit = () => {}
}) => {
  const handleClose = () => toggleModal({ show: false });
  const inputRef = useRef(null);

  const { Download, DownloadBulk, DownloadUnpacked, DownloadBulkUnpacked } =
    ActionsModalNames;
  if (
    [Download, DownloadBulk, DownloadUnpacked, DownloadBulkUnpacked].includes(
      modalName
    )
  ) {
    const unpacked =
      modalName === DownloadUnpacked || modalName === DownloadBulkUnpacked;
    const bulk =
      modalName === DownloadBulk || modalName === DownloadBulkUnpacked;
    const onSubmit = bulk ? onBulkDownload : onDownloadSample;

    const { mixedSamplesInSelection, noLocalSamplesInSelection } =
      activeModalData || {};

    return (
      <Modal
        show
        size="sm"
        onHide={handleClose}
        data-testid={`actions_modal_container_${snakeCase(modalName)}`}
      >
        <Modal.Header closeButton>
          <Modal.Title
            data-testid={`actions_modal_title_${snakeCase(modalName)}`}
          >
            Zip password
          </Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={e => {
            onSubmit(e, unpacked, activeModalData);
            handleClose();
          }}
        >
          <Modal.Body>
            <RenderWhen
              condition={mixedSamplesInSelection || noLocalSamplesInSelection}
            >
              <ActionsModalSelectionsMessage
                hasNoLocalSamples={noLocalSamplesInSelection}
                hasMixedSamples={mixedSamplesInSelection}
              />
            </RenderWhen>
            <Form.Control
              type="text"
              name={`download-sample-password${unpacked ? "-unpacked" : ""}`}
              autoFocus
              ref={inputRef}
              defaultValue={noLocalSamplesInSelection ? "" : "infected"}
              onFocus={() => inputRef?.current?.select()}
              data-testid={`actions_modal_input_${snakeCase(modalName)}`}
              disabled={noLocalSamplesInSelection}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="prompt-secondary"
              onClick={handleClose}
              data-testid={`actions_modal_cancel_btn_${snakeCase(modalName)}`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="prompt-primary"
              data-testid={`actions_modal_submit_btn_${snakeCase(modalName)}`}
              disabled={noLocalSamplesInSelection}
            >
              Download
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }

  if (modalName === ActionsModalNames.ReportFalseClassification) {
    return (
      <FalseReportModal
        show
        sha1={activeModalData.sha1}
        onClose={handleClose}
      />
    );
  }

  if (modalName === ActionsModalNames.ReportUrlFalseClassification) {
    return (
      <UrlFalseReportModal
        show
        url={activeModalData.url}
        onClose={handleClose}
      />
    );
  }

  if (modalName === ActionsModalNames.UserClassification) {
    return (
      <UserClassification
        onClose={handleClose}
        sampleHashes={[activeModalData.sha1]}
        refreshPage={activeModalData.refreshPage}
        onSuccess={onSubmit}
      />
    );
  }

  if (modalName === ActionsModalNames.BulkUserClassification) {
    return (
      <UserClassification
        onClose={handleClose}
        sampleHashes={activeModalData.hashes}
        refreshPage={activeModalData.refreshPage}
        onSuccess={onSubmit}
      />
    );
  }

  if (
    [
      ActionsModalNames.Subscribehashes,
      ActionsModalNames.Unsubscribehashes
    ].includes(modalName)
  ) {
    return (
      <Suspense fallback={<LoadingOverlay />}>
        <ManageHashSubscriptionDialog
          action={activeModalData.action}
          hashes={activeModalData.subscriptionHashes}
          samplesQuery={activeModalData.samplesQuery}
          extractedSampleQuery={activeModalData.extractedSampleQuery}
          onClose={handleClose}
        />
      </Suspense>
    );
  }

  if (modalName === ActionsModalNames.Reanalyze) {
    if (
      activeModalData.samples.length >
      parseInt(consts.max_number_samples_reanalyze, 10)
    ) {
      const message = `Warning: maximum number of samples that can be reanalyzed at once exceeded. If you proceed 
          only ${numeral(consts.max_number_samples_reanalyze).format(
            "0,0"
          )} samples will be reanalyzed!`;
      notifications.show({
        level: "warning",
        message
      });
    }
    return (
      <Reanalyze
        samples={activeModalData.samples}
        onClose={handleClose}
        onReanalyzePopupWindowSubmit={
          activeModalData.onReanalyzePopupWindowSubmit
        }
        initial={activeModalData.initialAnalysisSelections}
      />
    );
  }

  if (modalName === ActionsModalNames.Delete) {
    const { hash, hashes, all_uploads, onSuccess } = activeModalData || {};
    if (all_uploads) {
      return handleDeleteAll(activeModalData, onSuccess, handleClose);
    } else if (hash) {
      return handleDeleteSingle(hash, onSuccess, handleClose);
    } else if (hashes?.length) {
      return handleDeleteBulk(hashes, onSuccess, handleClose);
    }
  }

  if (modalName === ActionsModalNames.Recrawl) {
    return (
      <SubmitUrlModal
        key={activeModalData.url}
        url={activeModalData.url}
        customMessages={activeModalData.customMessages}
        show
        handleShow={handleClose}
      />
    );
  }

  if (modalName === ActionsModalNames.RetryURL) {
    return (
      <SubmitUrlModal
        url={activeModalData.url}
        customMessages={activeModalData.customMessages}
        show
        handleShow={handleClose}
      />
    );
  }

  if (modalName === ActionsModalNames.ApplyTags) {
    const {
      hashes,
      isSelectAll,
      sort,
      selections,
      query,
      selectedCountTotalOverLimit,
      mixedSamplesInSelection,
      isSearchPage
    } = activeModalData || {};
    return (
      <TagsModalChooser
        hashes={hashes}
        isSelectAll={isSelectAll}
        sort={sort}
        selections={selections}
        query={query}
        handleShow={handleClose}
        selectedCountTotalOverLimit={selectedCountTotalOverLimit}
        mixedSamplesInSelection={mixedSamplesInSelection}
        isSearchPage={isSearchPage}
      />
    );
  }

  if (modalName === ActionsModalNames.Visualization) {
    return (
      <PreviewVisualizationModal
        sha1={activeModalData.sha1}
        fileType={activeModalData.fileType}
        fileSubtype={activeModalData.fileSubtype}
        typeDisplay={activeModalData.formatIdentification}
        threatStatus={activeModalData.threatStatus}
        sourceId={activeModalData.sourceId}
        onHide={handleClose}
        initialRulesetName={activeModalData.rulesetName}
        initialRuleName={activeModalData.ruleName}
      />
    );
  }

  console.warn(`Unrecognized modal name ${modalName}`);
  return null;
};

export default ActionsModal;
